import { useState, type RefObject } from 'react';
import { FolderPlus } from 'lucide-react';
import { CreateCollectionSheet } from './CreateCollectionSheet';
import { Capacitor } from '@capacitor/core';
import { cn } from '@/lib/utils';
import { useScrollOffset } from '@/hooks/useScrollOffset';
import { triggerHaptic } from '@/hooks/useHaptics';

interface CreateCollectionFABProps {
  scrollRef?: RefObject<HTMLElement | null>;
  /** Hide the FAB with scale animation (e.g. during multi-select) */
  hidden?: boolean;
}

export function CreateCollectionFAB({ scrollRef, hidden }: CreateCollectionFABProps) {
  const [isOpen, setIsOpen] = useState(false);
  const fallbackRef = { current: null } as RefObject<HTMLElement | null>;
  const isScrolled = useScrollOffset(scrollRef || fallbackRef, 20);

  return (
    <>
      <div 
        className={cn(
          "fixed right-4 flex flex-col items-center gap-3 z-40 transition-all duration-300 origin-center",
          (isScrolled || hidden) && "scale-0 opacity-0"
        )}
        style={{ bottom: Capacitor.isNativePlatform()
          ? 'calc(5.25rem + env(safe-area-inset-bottom))'
          : '6.25rem'
        }}
      >
        <button
          onClick={() => {
            triggerHaptic('medium');
            setIsOpen(true);
          }}
          className="w-14 h-14 flex items-center justify-center active:scale-95 transition-transform rounded-full shadow-lg border border-primary/20 bg-brand-gradient"
          aria-label="Create collection"
        >
          <FolderPlus 
            className="w-7 h-7 text-primary-foreground"
            strokeWidth={2.5}
          />
        </button>
      </div>

      <CreateCollectionSheet 
        open={isOpen} 
        onOpenChange={setIsOpen}
      />
    </>
  );
}
